import type { AdvancedFilterData } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import {
  SortDirection,
  type TableSort,
} from "@redotech/redo-views-common/table";
import * as z from "zod";
import {
  keepsSupplyIntelWarehouse,
  summarizeSupplyIntelBrands,
  SUPPLY_INTEL_NO_WAREHOUSE,
  SupplyIntelBrandFilterName,
  SupplyIntelUnitTotalsSchema,
  totalSupplyIntelUnitRows,
  type SupplyIntelUnitTotals,
} from "./brand-list";
import type { SupplyIntelBrandUnitRow } from "./brand-units";
import { supplyIntelCategoryLabel } from "./category";
import type { SupplyIntelConfig } from "./config";

/** How the table names units with no category. */
export const SUPPLY_INTEL_NO_CATEGORY_LABEL = "No category";

/** Category paths roll up to this many levels, such as "Apparel & Accessories > Clothing > Pants". */
const SUPPLY_INTEL_CATEGORY_LEVELS = 3;

/** The category a path rolls up to; null without a category. */
function supplyIntelCategoryGroup(path: string | null): string | null {
  return path === null
    ? null
    : path.split(" > ").slice(0, SUPPLY_INTEL_CATEGORY_LEVELS).join(" > ");
}

const SupplyIntelCategoryWarehouseTotalsSchema = z.object({
  /** A warehouse ID, or {@link SUPPLY_INTEL_NO_WAREHOUSE}. */
  warehouseId: z.string(),
  /** Brands with units in the category in this warehouse. */
  accountIds: z.array(z.string()),
  totals: SupplyIntelUnitTotalsSchema,
});

const SupplyIntelCategoryListRowSchema_ = z.object({
  /** The rolled-up category path; null for units with no category. */
  category: z.string().nullable(),
  byWarehouse: z.array(SupplyIntelCategoryWarehouseTotalsSchema),
});
/** A category's totals across brands as the snapshot stores them, split by warehouse for the warehouse filter. */
export const SupplyIntelCategoryListRowSchema: z.ZodType<SupplyIntelCategoryListRow> =
  SupplyIntelCategoryListRowSchema_;
export interface SupplyIntelCategoryListRow extends z.infer<
  typeof SupplyIntelCategoryListRowSchema_
> {}

const SupplyIntelCategoryRowSchema_ = z.object({
  category: z.string().nullable(),
  /** Brands with units in the category, in the selected warehouses. */
  brands: z.number(),
  totals: SupplyIntelUnitTotalsSchema,
});
export const SupplyIntelCategoryRowSchema: z.ZodType<SupplyIntelCategoryRow> =
  SupplyIntelCategoryRowSchema_;
export interface SupplyIntelCategoryRow extends z.infer<
  typeof SupplyIntelCategoryRowSchema_
> {}

/**
 * Collects category totals across brands as each brand is built, using that
 * brand's config so its own resale prices count. `rows()` returns one row per
 * category, split by warehouse.
 */
export class SupplyIntelCategoryTotals {
  private readonly byCategory = new Map<
    string | null,
    Map<string, { accountIds: Set<string>; totals: SupplyIntelUnitTotals[] }>
  >();

  add(
    accountId: string,
    rows: readonly SupplyIntelBrandUnitRow[],
    config: SupplyIntelConfig,
  ): void {
    const groups = new Map<
      string | null,
      Map<string, SupplyIntelBrandUnitRow[]>
    >();
    for (const row of rows) {
      const category = supplyIntelCategoryGroup(row.category);
      const warehouseId = row.warehouseId ?? SUPPLY_INTEL_NO_WAREHOUSE;
      const byWarehouse =
        groups.get(category) ?? new Map<string, SupplyIntelBrandUnitRow[]>();
      groups.set(category, byWarehouse);
      const warehouseRows = byWarehouse.get(warehouseId);
      if (warehouseRows) {
        warehouseRows.push(row);
      } else {
        byWarehouse.set(warehouseId, [row]);
      }
    }
    for (const [category, rowsByWarehouse] of groups) {
      const byWarehouse =
        this.byCategory.get(category) ??
        new Map<
          string,
          { accountIds: Set<string>; totals: SupplyIntelUnitTotals[] }
        >();
      this.byCategory.set(category, byWarehouse);
      for (const [warehouseId, groupRows] of rowsByWarehouse) {
        const entry = byWarehouse.get(warehouseId) ?? {
          accountIds: new Set<string>(),
          totals: [],
        };
        byWarehouse.set(warehouseId, entry);
        entry.accountIds.add(accountId);
        entry.totals.push(totalSupplyIntelUnitRows(groupRows, config));
      }
    }
  }

  rows(): SupplyIntelCategoryListRow[] {
    return [...this.byCategory].map(([category, byWarehouse]) => ({
      category,
      byWarehouse: [...byWarehouse].map(
        ([warehouseId, { accountIds, totals }]) => ({
          warehouseId,
          accountIds: [...accountIds].sort(),
          totals: summarizeSupplyIntelBrands(totals),
        }),
      ),
    }));
  }
}

export enum SupplyIntelCategorySortKey {
  ActionableValueUsd = "actionableValueUsd",
  Brands = "brands",
  Category = "category",
  ExcludedUnits = "excludedUnits",
  LiquidationFloorUsd = "liquidationFloorUsd",
  RedoMarketplaceResaleBufferUsd = "redoMarketplaceResaleBufferUsd",
  RedoMarketplaceResaleBufferUsd30 = "redoMarketplaceResaleBufferUsd30",
  RedoMarketplaceResaleBufferUsd50 = "redoMarketplaceResaleBufferUsd50",
  ResaleBufferUsd = "resaleBufferUsd",
  ResaleBufferUsd30 = "resaleBufferUsd30",
  ResaleBufferUsd50 = "resaleBufferUsd50",
  ResaleUnits = "resaleUnits",
  Units = "units",
  ValueAtListUsd = "valueAtListUsd",
}

/** Where resellable value sits: categories by store value to sell. */
export const DEFAULT_SUPPLY_INTEL_CATEGORY_SORT: TableSort<SupplyIntelCategorySortKey> =
  {
    key: SupplyIntelCategorySortKey.ActionableValueUsd,
    direction: SortDirection.DESC,
  };

/** What category search matches: the path, or "No category" as the table shows it. */
function categorySearchText(category: string | null): string {
  return (category ?? SUPPLY_INTEL_NO_CATEGORY_LABEL).toLowerCase();
}

function categoryText(row: SupplyIntelCategoryRow): string {
  return row.category === null ? "" : supplyIntelCategoryLabel(row.category);
}

function compareCategories(
  a: SupplyIntelCategoryRow,
  b: SupplyIntelCategoryRow,
  key: SupplyIntelCategorySortKey,
): number {
  switch (key) {
    case SupplyIntelCategorySortKey.Category:
      return categoryText(a).localeCompare(categoryText(b));
    case SupplyIntelCategorySortKey.Brands:
      return a.brands - b.brands;
    case SupplyIntelCategorySortKey.ActionableValueUsd:
    case SupplyIntelCategorySortKey.ExcludedUnits:
    case SupplyIntelCategorySortKey.LiquidationFloorUsd:
    case SupplyIntelCategorySortKey.RedoMarketplaceResaleBufferUsd:
    case SupplyIntelCategorySortKey.RedoMarketplaceResaleBufferUsd30:
    case SupplyIntelCategorySortKey.RedoMarketplaceResaleBufferUsd50:
    case SupplyIntelCategorySortKey.ResaleBufferUsd:
    case SupplyIntelCategorySortKey.ResaleBufferUsd30:
    case SupplyIntelCategorySortKey.ResaleBufferUsd50:
    case SupplyIntelCategorySortKey.ResaleUnits:
    case SupplyIntelCategorySortKey.Units:
    case SupplyIntelCategorySortKey.ValueAtListUsd:
      return a.totals[key] - b.totals[key];
  }
}

export interface SupplyIntelCategoryQuery {
  search?: string;
  advancedFiltersData: readonly AdvancedFilterData[];
  sort: TableSort<SupplyIntelCategorySortKey>;
  page: number;
  pageSize: number;
}

/**
 * Search, sort and page the categories, each scoped to the warehouses the
 * warehouse filters keep; the brand list's other filters describe brands, so
 * they don't apply here.
 */
export function querySupplyIntelCategories(
  listRows: readonly SupplyIntelCategoryListRow[],
  query: SupplyIntelCategoryQuery,
): { rows: SupplyIntelCategoryRow[]; totalCount: number } {
  const warehouseFilters = query.advancedFiltersData.filter(
    (filter) => filter.name === SupplyIntelBrandFilterName.Warehouse,
  );
  const search = query.search?.trim().toLowerCase();
  const rows = listRows.flatMap((row): SupplyIntelCategoryRow[] => {
    const selected = row.byWarehouse.filter(({ warehouseId }) =>
      warehouseFilters.every((filter) =>
        keepsSupplyIntelWarehouse(filter, warehouseId),
      ),
    );
    if (
      selected.length === 0 ||
      (search && !categorySearchText(row.category).includes(search))
    ) {
      return [];
    }
    return [
      {
        category: row.category,
        brands: new Set(selected.flatMap(({ accountIds }) => accountIds)).size,
        totals: summarizeSupplyIntelBrands(
          selected.map(({ totals }) => totals),
        ),
      },
    ];
  });
  const direction = query.sort.direction === SortDirection.ASC ? 1 : -1;
  const sorted = rows.sort(
    (a, b) =>
      direction * compareCategories(a, b, query.sort.key) ||
      categoryText(a).localeCompare(categoryText(b)),
  );
  const start = query.page * query.pageSize;
  return {
    rows: sorted.slice(start, start + query.pageSize),
    totalCount: sorted.length,
  };
}
